import React, { useState } from 'react';
import axios from 'axios';
import { FileXls, FilePdf, Download } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ExportReportDialog = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(null); // 'xlsx' | 'pdf' | null
  const [filters, setFilters] = useState({
    fecha_desde: '',
    fecha_hasta: '',
    estado: 'todas',
    area: ''
  });

  const handleChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const download = async (format) => {
    setLoading(format);
    try {
      const params = {};
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      if (filters.estado && filters.estado !== 'todas') params.estado = filters.estado;
      if (filters.area) params.area = filters.area;

      const response = await axios.get(`${API_URL}/api/reportes/${format}`, {
        params,
        withCredentials: true,
        responseType: 'blob'
      });

      // Trigger download in browser
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'] || '';
      const match = contentDisposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `movilizaciones.${format}`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Reporte ${format.toUpperCase()} descargado`);
      setOpen(false);
    } catch (error) {
      // When responseType is 'blob', the error body arrives as a Blob; try to parse it as JSON.
      let detail = null;
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          detail = parsed?.detail;
        } catch (_e) {
          detail = null;
        }
      } else {
        detail = error.response?.data?.detail;
      }
      const msg = typeof detail === 'string' ? detail : (error.message || 'Error al generar el reporte');
      toast.error(msg);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="open-export-dialog-button"
          variant="outline"
          className="gap-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white font-['IBM_Plex_Sans'] h-12 px-5 transition-colors"
        >
          <Download size={18} weight="bold" />
          Exportar Reporte
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-md bg-white border border-[#E9ECEF]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
            Exportar Reporte
          </DialogTitle>
          <DialogDescription className="text-[#495057] font-['IBM_Plex_Sans']">
            Filtra las movilizaciones y descarga el reporte en Excel o PDF
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="fecha_desde" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A] text-sm">
                Desde
              </Label>
              <Input
                id="fecha_desde"
                data-testid="report-fecha-desde"
                type="date"
                value={filters.fecha_desde}
                onChange={(e) => handleChange('fecha_desde', e.target.value)}
                className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fecha_hasta" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A] text-sm">
                Hasta
              </Label>
              <Input
                id="fecha_hasta"
                data-testid="report-fecha-hasta"
                type="date"
                value={filters.fecha_hasta}
                onChange={(e) => handleChange('fecha_hasta', e.target.value)}
                className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="estado" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A] text-sm">
              Estado
            </Label>
            <Select value={filters.estado} onValueChange={(v) => handleChange('estado', v)}>
              <SelectTrigger data-testid="report-estado-select" className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']">
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todos los estados</SelectItem>
                <SelectItem value="agendado">Agendado</SelectItem>
                <SelectItem value="en_espera_asignacion">En Espera de Asignación</SelectItem>
                <SelectItem value="asignado">Asignado</SelectItem>
                <SelectItem value="realizada">Realizada</SelectItem>
                <SelectItem value="suspendida">Suspendida</SelectItem>
                <SelectItem value="rechazado">Rechazado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="area" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A] text-sm">
              Área (opcional)
            </Label>
            <Input
              id="area"
              data-testid="report-area-input"
              type="text"
              value={filters.area}
              onChange={(e) => handleChange('area', e.target.value)}
              placeholder="Ej: Marketing, IT, Ventas..."
              className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              data-testid="download-xlsx-button"
              onClick={() => download('xlsx')}
              disabled={loading !== null}
              className="flex-1 gap-2 bg-[#0A6E3F] hover:bg-[#085530] text-white font-['IBM_Plex_Sans'] transition-colors"
            >
              <FileXls size={18} weight="bold" />
              {loading === 'xlsx' ? 'Generando...' : 'Descargar Excel'}
            </Button>
            <Button
              data-testid="download-pdf-button"
              onClick={() => download('pdf')}
              disabled={loading !== null}
              className="flex-1 gap-2 bg-[#B21A1A] hover:bg-[#8f1414] text-white font-['IBM_Plex_Sans'] transition-colors"
            >
              <FilePdf size={18} weight="bold" />
              {loading === 'pdf' ? 'Generando...' : 'Descargar PDF'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
